import type { SipCalculatorResult } from "./SipCalculatorCalculations";
import type { SliderKind } from "./SipCalculatorTypes";

export const parseValue = (value: string): number => {
  const parsedValue = Number(value.replace(/[^\d.]/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const clampValue = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const normalizeInitialNumericValue = (value: number, min: number, max: number): string =>
  String(clampValue(value, min, max));

export const sanitizeNumericInput = (value: string, decimalAllowed = false): string => {
  const normalized = value.replace(/[^\d.]/g, "");

  if (!decimalAllowed) {
    return normalized.replace(/\./g, "");
  }

  const [whole = "", ...fractions] = normalized.split(".");
  return fractions.length ? `${whole}.${fractions.join("")}` : normalized;
};

export const hasAllCalculatorInputValues = (
  investmentInput: string,
  tenureInput: string,
  interestRateInput: string
): boolean =>
  investmentInput.trim() !== "" && tenureInput.trim() !== "" && interestRateInput.trim() !== "";

export const isTenureEmptyOrZero = (tenureInput: string): boolean => {
  const trimmed = tenureInput.trim();
  if (trimmed === "") return true;
  return parseValue(trimmed) === 0;
};

export const hasEffectiveTenureValue = (tenureInput: string): boolean =>
  !isTenureEmptyOrZero(tenureInput);

export const isFieldInputValid = (input: string, min: number, max: number): boolean => {
  if (input.trim() === "") return false;

  const value = parseValue(input);
  return value >= min && value <= max;
};

export const resolveCalculatorFieldValue = (
  hasValue: boolean,
  isValid: boolean,
  currentValue: number,
  lastValidValue: number | undefined
): number | null => {
  if (!hasValue) return null;
  if (isValid) return currentValue;
  if (lastValidValue !== undefined) return lastValidValue;
  return null;
};

/** Years shown in the lumpsum growth sentence when tenure is missing or invalid. */
export const resolveSummaryGrowthYears = (
  hasValue: boolean,
  isValid: boolean,
  parsedValue: number,
  min: number,
  max: number,
  lastValidValue: number | null
): number => {
  if (!hasValue) return 0;
  if (isValid) return parsedValue;
  if (parsedValue < min) return parsedValue;
  return lastValidValue ?? parsedValue;
};

export const hasValidCalculatorInputs = (
  investmentInput: string,
  tenureInput: string,
  interestRateInput: string,
  bounds: {
    investmentMin: number;
    investmentMax: number;
    tenureMin: number;
    tenureMax: number;
    interestRateMin: number;
    interestRateMax: number;
  }
): boolean => {
  if (
    investmentInput.trim() === "" ||
    tenureInput.trim() === "" ||
    interestRateInput.trim() === ""
  ) {
    return false;
  }

  const investment = parseValue(investmentInput);
  const tenure = parseValue(tenureInput);
  const rate = parseValue(interestRateInput);

  return (
    investment >= bounds.investmentMin &&
    investment <= bounds.investmentMax &&
    tenure >= bounds.tenureMin &&
    tenure <= bounds.tenureMax &&
    rate >= bounds.interestRateMin &&
    rate <= bounds.interestRateMax
  );
};

export const buildMarkerValues = (min: number, max: number): number[] => {
  if (max <= min) return [min, max];
  return [min, max];
};

export const resolveSliderMarkers = (
  markers: readonly number[],
  min: number,
  max: number
): number[] => markers.filter((value) => value >= min && value <= max);

export const valueToSliderPercent = (
  value: number,
  min: number,
  max: number,
  useLogScale = false
): number => {
  if (max <= min) return 0;

  const clamped = Math.max(min, Math.min(max, value));

  if (useLogScale && min > 0) {
    return ((Math.log(clamped) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 100;
  }

  return ((clamped - min) / (max - min)) * 100;
};

export const interpolateRangeFraction = (
  value: number,
  start: number,
  end: number,
  useLogScale = false
): number => {
  if (end <= start) return 0;

  const clamped = Math.max(start, Math.min(end, value));

  if (useLogScale && start > 0) {
    return (Math.log(clamped) - Math.log(start)) / (Math.log(end) - Math.log(start));
  }

  return (clamped - start) / (end - start);
};

export const valueToEvenMarkerPercent = (
  value: number,
  markers: number[],
  min: number,
  max: number,
  useLogScale = false
): number => {
  if (markers.length < 2) {
    return valueToSliderPercent(value, min, max, useLogScale);
  }

  const clamped = Math.max(min, Math.min(max, value));
  const lastIndex = markers.length - 1;
  const firstMarker = markers[0];
  const lastMarker = markers[lastIndex];
  if (firstMarker === undefined || lastMarker === undefined) {
    return valueToSliderPercent(value, min, max, useLogScale);
  }

  if (clamped <= firstMarker) return 0;
  if (clamped >= lastMarker) return 100;

  for (let i = 0; i < lastIndex; i++) {
    const start = markers[i];
    const end = markers[i + 1];
    if (start === undefined || end === undefined) continue;
    if (clamped <= end) {
      const fraction = interpolateRangeFraction(clamped, start, end, useLogScale);
      return ((i + fraction) / lastIndex) * 100;
    }
  }

  return 100;
};

/** Maps slider % back to a value using evenly spaced marker segments. */
export const evenMarkerPercentToValue = (
  percent: number,
  markers: number[],
  min: number,
  max: number,
  step: number,
  useLogScale = false
): number => {
  if (markers.length < 2) {
    const clampedPercent = Math.max(0, Math.min(100, percent));
    if (useLogScale && min > 0) {
      const logValue = Math.log(min) + (clampedPercent / 100) * (Math.log(max) - Math.log(min));
      return Math.max(min, Math.min(max, Math.round(Math.exp(logValue) / step) * step));
    }
    const rawValue = min + (clampedPercent / 100) * (max - min);
    return Math.max(min, Math.min(max, Math.round(rawValue / step) * step));
  }

  const clampedPercent = Math.max(0, Math.min(100, percent));
  const lastIndex = markers.length - 1;
  const position = (clampedPercent / 100) * lastIndex;
  const index = Math.min(Math.floor(position), lastIndex - 1);
  const fraction = position - index;
  const start = markers[index];
  const end = markers[index + 1];
  if (start === undefined || end === undefined) {
    const rawValue = min + (clampedPercent / 100) * (max - min);
    return Math.max(min, Math.min(max, Math.round(rawValue / step) * step));
  }

  let rawValue: number;
  if (useLogScale && start > 0) {
    rawValue = Math.exp(Math.log(start) + fraction * (Math.log(end) - Math.log(start)));
  } else {
    rawValue = start + fraction * (end - start);
  }

  const normalized = step >= 1 ? Math.round(rawValue) : Math.round(rawValue / step) * step;
  return Math.max(min, Math.min(max, normalized));
};

const formatNumberWithDecimals = (value: number): string => {
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
};

const formatCurrencyScale = (value: number, threshold: number, suffix: string): string | null => {
  if (value >= threshold) {
    const scaled = value / threshold;
    const formatted = Math.round(scaled * 10) / 10;
    return `${formatNumberWithDecimals(formatted)}${suffix}`;
  }
  return null;
};

export const formatMarkerLabel = (value: number, kind: SliderKind): string => {
  if (kind === "percent") {
    return String(Math.round(value));
  }

  if (kind === "currency") {
    return (
      formatCurrencyScale(value, 10000000, "Cr") ??
      formatCurrencyScale(value, 100000, "L") ??
      formatCurrencyScale(value, 1000, "K") ??
      String(Math.round(value))
    );
  }

  return String(Math.round(value));
};

export const buildGrowthPercentage = (result: SipCalculatorResult, percentSymbol = "%"): string => {
  if (!result.investedAmount) {
    return `0${percentSymbol}`;
  }

  return `${Math.round((result.estimatedReturns / result.investedAmount) * 100)}${percentSymbol}`;
};

export const buildYearSequence = (tenureYears: number): number[] =>
  Array.from({ length: Math.max(1, Math.round(tenureYears)) }, (_, index) => index + 1);

/**
 * Formats a number string with Indian comma formatting (e.g., 1,00,00,000)
 * @param value - The number string (without commas) to format
 * @returns The formatted string with Indian commas
 */
export const formatIndianNumber = (value: string): string => {
  const numericValue = value.replace(/[^\d]/g, "");
  if (!numericValue) return "";

  const length = numericValue.length;
  if (length <= 3) return numericValue;

  // Indian format: last 3 digits, then groups of 2
  const lastThree = numericValue.slice(-3);
  const remaining = numericValue.slice(0, -3);

  const groups: string[] = [];
  let index = remaining.length;
  while (index > 0) {
    const start = Math.max(0, index - 2);
    groups.unshift(remaining.slice(start, index));
    index = start;
  }

  return `${groups.join(",")},${lastThree}`;
};

const extractDigits = (value: string): string => {
  let digits = "";
  for (let index = 0; index < value.length; index += 1) {
    const char = value.charAt(index);
    if (char >= "0" && char <= "9") {
      digits += char;
    }
  }
  return digits;
};

/**
 * Removes leading zeros from a digit string while preserving a lone "0".
 * e.g. "0224" → "224", "0" → "0", "100" → "100"
 */
export const stripLeadingZeros = (value: string): string => {
  const digits = extractDigits(value);
  if (!digits) return "";
  if (digits.length === 1) return digits;

  let start = 0;
  while (start < digits.length - 1 && digits.charAt(start) === "0") {
    start += 1;
  }
  return digits.slice(start);
};

export interface SanitizeCurrencyDigitsResult {
  value: string;
  rejected: boolean;
  moveCursorToEnd: boolean;
}

/**
 * Sanitizes currency digit input for investment fields.
 * Rejects appending digits while the field is lone "0" (user must clear first).
 */
export const sanitizeCurrencyDigits = (
  previousValue: string,
  nextRawValue: string
): SanitizeCurrencyDigitsResult => {
  const digitsOnly = extractDigits(nextRawValue);
  const previousDigits = extractDigits(previousValue);

  if (!digitsOnly) {
    return { value: "", rejected: false, moveCursorToEnd: false };
  }

  if (previousDigits === "0" && digitsOnly.length > 1 && digitsOnly.startsWith("0")) {
    return { value: previousDigits, rejected: true, moveCursorToEnd: true };
  }

  const normalized = stripLeadingZeros(digitsOnly);
  return {
    value: normalized,
    rejected: false,
    moveCursorToEnd: normalized !== digitsOnly,
  };
};

export interface SanitizeTenureOrPercentDigitsResult {
  value: string;
  moveCursorToEnd: boolean;
}

/**
 * Allows a single leading zero for tenure/rate fields (e.g. "02").
 * Extra leading zeros collapse to one and move the cursor to the end.
 */
export const sanitizeTenureOrPercentDigits = (
  nextRawValue: string
): SanitizeTenureOrPercentDigitsResult => {
  const digits = extractDigits(nextRawValue);
  if (!digits) {
    return { value: "", moveCursorToEnd: false };
  }

  let leadingZeroCount = 0;
  while (leadingZeroCount < digits.length && digits.charAt(leadingZeroCount) === "0") {
    leadingZeroCount += 1;
  }

  const rest = digits.slice(leadingZeroCount);
  if (!rest) {
    return { value: "0", moveCursorToEnd: leadingZeroCount > 1 };
  }

  if (leadingZeroCount > 1) {
    return { value: `0${rest}`, moveCursorToEnd: true };
  }

  if (leadingZeroCount === 1) {
    return { value: `0${rest}`, moveCursorToEnd: false };
  }

  return { value: rest, moveCursorToEnd: false };
};

/**
 * Gets the count of digits in a string (excluding commas and other non-numeric characters)
 * @param value - The string to count digits from
 * @returns The number of digits
 */
export const getDigitCount = (value: string): number => {
  return value.replace(/[^\d]/g, "").length;
};

/**
 * Restricts the input to a maximum number of digits
 * @param value - The input string
 * @param maxDigits - Maximum allowed digits
 * @returns The restricted string with only numeric characters up to maxDigits
 */
export const restrictToMaxDigits = (value: string, maxDigits: number): string => {
  const numericOnly = value.replace(/[^\d]/g, "");
  return numericOnly.slice(0, maxDigits);
};
